import { useEffect, useMemo, useState } from 'react';
import { validateAuthForm } from './validateAuthForm';
import { IAuthErrors, ISignInResponse } from './types';
import { signIn, signIn2FA } from 'src/services/client';
import { useDispatch } from 'react-redux';

import { useHistory } from 'react-router-dom';
import { handleSignIn } from './handleSignIn';
import { toast } from 'react-toastify';

export const useAuth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [show2FA, setShow2FA] = useState(false);

  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const history = useHistory();

  const [twoFaToken, setTwoFaToken] = useState('');

  const clearAll = () => {
    setEmail('');
    setPassword('');
    setTermsAndConditions(false);
    setTwoFACode('');
    setShow2FA(false);
    setTwoFaToken('');
  };

  const [errors, setErrors] = useState<IAuthErrors>({
    email: {
      invalid: false,
      message: '',
    },
    password: {
      invalid: false,
      message: '',
    },
    termsAndConditions: {
      invalid: false,
      message: '',
    },
  });

  const validateForm = () => {
    const newErrors = validateAuthForm(email, password, termsAndConditions);
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error.invalid);
  };

  const submit = () => {
    if (validateForm()) {
      // Submit the form :))
      setLoading(true);
      signIn({
        Username: email.trim(),
        Password: password.trim(),
      })
        .then(async (res) => {
          if (!(res instanceof Response)) return;
          const data = await res.json();

          if (res.ok && res.status === 200) {
            return handleSignIn(data, dispatch, history);
          }

          if (res.status === 401 && data.data?.requiresTwoFactor) {
            setShow2FA(true);
            setTwoFaToken(data.data.auth);
            return;
          }

          toast.error('🚫 Login failed, invalid email or password', {
            autoClose: 12000,
            position: 'top-center',
          });
        })
        .catch((error) => {
          console.error('Sign-in error:', error);
          toast.error('Unknown Error, please try again later.', {
            autoClose: false,
            position: 'top-center',
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const submitTwoFA = () => {
    setLoading(true);
    signIn2FA(twoFACode, twoFaToken)
      .then(async (res) => {
        if (!(res instanceof Response)) return;
        const data = (await res.json()) as ISignInResponse;
        if (res.ok) {
          handleSignIn(data, dispatch, history);
        } else {
          toast.error(
            '🚫 Login failed, invalid two factor authentication code',
            {
              autoClose: 12000,
              position: 'top-center',
            }
          );
          clearAll();
          console.error('Two-factor authentication failed:', res);
        }
      })
      .catch((error) => {
        console.error('Two-factor authentication error:', error);
        toast.error('Unknown Error, please try again later.', {
          autoClose: false,
          position: 'top-center',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(false);

    //TODO: Outdated method of handling browser back button on auth
    const { href } = window.location;
    if (href.indexOf('sign-in') > -1) {
      window.history.pushState(null, null, '');
      window.addEventListener('popstate', function (event) {
        window.history.pushState(null, null, '');
      });
    }
  }, []);

  return {
    email,
    setEmail,

    password,
    setPassword,

    termsAndConditions,
    setTermsAndConditions,

    twoFACode,
    setTwoFACode,

    errors,

    show2FA,

    loading,

    submit,
    submitTwoFA,
  };
};
